import { useState, useEffect } from 'react';
import { Flex, Center, Icon, Box, Spinner } from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import { Expand04 } from '@kula-ai/icons/outline/Expand04';
import { Move } from '@kula-ai/icons/outline/Move';
import { KModal, KText } from '@/ui-library/components';
import { SearchFilter } from '@/components/ats/filters/components/SearchFilter';
import { DrilldownBreadcrumb } from '@/pages/reports/ats-reports/drilldown/DrilldownBreadCrumb';
import { getDrilldownContext } from '@/pages/reports/ats-reports/drilldown/getDrilldownContext';
import { ActivitiesTable } from '../shared/activity-popup/activity-table';
import { drilldownContentATS } from '@/pages/reports/non-ats-reports/shared/activity-popup/drilldown-content';
import { Minimize01 } from '@kula-ai/icons/outline/Minimize01';
import { EmptyReportChart } from '@/pages/reports/custom-reports/components/ReportCharts/EmptyReportChart';
import { throttle } from 'lodash-es';
import { useKFlags } from '@/hooks/use-k-flags';
import { getDrilldownParams } from '@/store/reports/flow-health/candidate-touchpoint/actions';
import { DownloadData } from '@/pages/reports/ats-reports/drilldown/DownloadData';

// import { dateParser } from '../shared/dateParser';

type DrilldownActivityPopupProps = {
  isOpen?: boolean;
  onClose?: () => void;
  filters?: any;
  selected?: { id?: string; row?: { original: any }; type?: string };
  filterParams?: any;
  fetchDrilldownAction: (params: any) => any;
  drilldownData: any[];
  isLoading: boolean;
  currentDrillPage: number;
  totalCount: number;
  applySearch: (params: any) => any;
  searchTerm: string;
  useColumnConfig?: any;
  resetDrill: () => void;
  initialLoading?: boolean;
  tile?: any;
};

// Custom Select Value Container Component
// const DownloadValueContainer = ({ children, ...props }) => (
//   <components.ValueContainer {...props}>
//     <Flex alignItems="center" gap="4px">
//       <Icon as={Download01} color="secondary-700" />
//       <Box>{children}</Box>
//     </Flex>
//   </components.ValueContainer>
// );

// Drilldown Header Component
const DrilldownHeader = ({
  selected,
  totalCount,
  modalSize,
  onToggleModalSize,
  onMouseDown,
}) => {
  const {
    group_by = null,
    type = '',
    yAxis = '',
    dt = '',
    g1 = '',
  } = selected?.row?.original || {};

  const groups = selected?.row?.original;

  const drilldownContext = {
    type: type || selected?.type,
    metric: drilldownContentATS?.[selected?.id] || 'Activity Details',
    totalCount: totalCount || 0,
    yAxis: yAxis,
    xAxis: dt,
    chartValue: g1,
    g1Header: groups?.parent
      ? groups?.parent?.name || groups?.parent?.title || groups?.parent
      : group_by?.name || group_by?.title || group_by,
    g2Header: groups?.parent
      ? group_by?.name || group_by?.title || group_by
      : null,
    metricValue: groups[selected?.id],
  };

  // to-do
  // 	g1: selected?.row?.original?.parent
  //   ? dateParser([selected.row.original.parent], 'W')[0] ||
  //     selected.row.original.parent
  //   : dateParser([group_by], 'W')[0] || group_by[0] || group_by,
  // g2: selected?.row?.original?.parent
  //   ? dateParser([group_by], 'W')[0] || group_by[0] || group_by
  //   : undefined,

  return (
    <Flex
      mt="3px"
      mr="20px"
      gap="20px"
      alignItems="center"
      justifyContent="space-between"
    >
      <Box maxW={`calc(100% - 50px)`}>
        <DrilldownBreadcrumb context={drilldownContext} />
      </Box>
      <Flex gap="8px">
        {modalSize !== 'full' && (
          <Icon
            as={Move}
            w="14px"
            h="14px"
            cursor="grab"
            onMouseDown={onMouseDown}
          />
        )}
        <Icon
          as={modalSize === 'full' ? Minimize01 : Expand04}
          cursor="pointer"
          w="14px"
          h="14px"
          onClick={onToggleModalSize}
        />
      </Flex>
    </Flex>
  );
};

// Filter Bar Component
const FilterBar = ({
  searchTerm,
  onSearchChange,
  drilldownData,
  reportData = {},
  initialLoading,
}) => {
  const showSearchFilter = drilldownData?.length > 0 || searchTerm;
  const { reportsV2 } = useKFlags();

  return (
    <>
      <Flex
        justifyContent="end"
        py="10px"
        borderTopWidth="1px"
        borderColor="border-color"
        gap="6px"
      >
        {showSearchFilter && (
          <SearchFilter
            isCustomSearch={true}
            onCustomSearch={onSearchChange}
            searchTerm={searchTerm || ''}
            inputStyles={{
              bg: 'white',
            }}
          />
        )}

        {((reportsV2 && !initialLoading) || searchTerm) && (
          <DownloadData
            reportData={reportData}
            drilldownData={drilldownData}
            isDisabled={!showSearchFilter || initialLoading}
          />
        )}
      </Flex>
    </>
  );
};

// Content Area Component
const ContentArea = ({
  isLoading,
  currentDrillPage,
  drilldownData,
  totalCount,
  handleLoadMore,
  modalSize,
  columnConfig,
  initialLoading,
}) => (
  <Flex
    height={
      modalSize === 'full' ? 'calc(100vh - 150px)' : 'calc(100vh - 400px)'
    }
    overflow="auto"
  >
    {initialLoading ? (
      <Center flex="1">
        <Spinner color="primary-800" />
      </Center>
    ) : drilldownData?.length === 0 ? (
      <EmptyReportChart text="No data found" subText=" " />
    ) : (
      <ActivitiesTable
        data={drilldownData}
        totalCount={totalCount}
        currentDrillPage={currentDrillPage}
        handleLoadMore={handleLoadMore}
        columnConfig={columnConfig}
        isLoadingMore={isLoading}
      />
    )}
  </Flex>
);

// Main DrilldownActivityPopup Component
export const DrilldownActivityPopup = ({
  isOpen = true,
  onClose = () => {},
  filters = {},
  selected = {},
  filterParams = undefined,
  fetchDrilldownAction,
  drilldownData = [],
  isLoading = false,
  currentDrillPage = 1,
  totalCount = 0,
  applySearch = () => {},
  resetDrill = () => {},
  searchTerm = '',
  useColumnConfig,
  initialLoading = false,
  tile,
}: DrilldownActivityPopupProps) => {
  const dispatch = useDispatch();
  const [modalSize, setModalSize] = useState('5xl');
  const displayedItems = Math.min(currentDrillPage * 50, totalCount);

  const [sortParams, setSortParams] = useState({
    sort_by_column: 'candidate_name',
    sort_by_direction: 'asc',
  });
  const { columns: columnConfig } = useColumnConfig({
    onSort: setSortParams,
    currentSort: sortParams,
    resetDrill: resetDrill,
  });

  // Build API parameters for data fetching
  const buildApiParams = (page: number) => {
    const apiParams: {
      filters: any;
      selected: any;
      filterParams?: any;
      page: number;
      searchTerm?: string;
      sort_by_direction: string;
    } = {
      filters,
      selected,
      page,
      searchTerm,
      sort_by_direction: sortParams.sort_by_direction,
    };

    if (filterParams !== undefined) {
      apiParams.filterParams = filterParams;
    }

    return apiParams;
  };

  // Fetch initial data when component mounts or search changes
  useEffect(() => {
    if (!isOpen || !filters || !selected) return;

    const apiParams = buildApiParams(1);
    dispatch(fetchDrilldownAction(apiParams));

    if (!isOpen) {
      setModalSize('xl');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, searchTerm, sortParams]);

  // Event handlers
  const handleLoadMore = throttle(() => {
    if (isLoading) return;
    if (displayedItems >= totalCount) return;
    const apiParams = buildApiParams(currentDrillPage + 1);
    dispatch(fetchDrilldownAction(apiParams));
  }, 3000);

  const handleMouseDown = (e) => {
    e.preventDefault();

    const modal = e.currentTarget.closest('.chakra-modal__content');
    if (!modal) return;

    const startX = e.clientX;
    const startY = e.clientY;
    const { left, top } = modal.getBoundingClientRect();

    modal.style.cursor = 'grabbing';

    const onMouseMove = (moveEvent) => {
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;

      let newLeft = left + dx;
      let newTop = top + dy;

      const modalWidth = modal.offsetWidth;
      const modalHeight = modal.offsetHeight;
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      // Prevent dragging out of bounds
      newLeft = Math.max(0, Math.min(newLeft, windowWidth - modalWidth));
      newTop = Math.max(0, Math.min(newTop, windowHeight - modalHeight));

      modal.style.position = 'fixed';
      modal.style.left = `${newLeft}px`;
      modal.style.top = `${newTop}px`;
      modal.style.margin = '0';
      modal.style.transform = 'none';
    };

    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      modal.style.cursor = 'default';
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const toggleModalSize = () => {
    const modal = document.querySelector(
      '.chakra-modal__content'
    ) as HTMLElement;

    const isGoingFull = modalSize !== 'full';
    setModalSize(isGoingFull ? 'full' : '5xl');

    if (isGoingFull && modal) {
      modal.style.position = '';
      modal.style.left = '';
      modal.style.top = '';
      modal.style.margin = '';
      modal.style.transform = '';
    }
  };

  const handleSearchChange = (searchTermValue) => {
    if (searchTermValue !== searchTerm) {
      dispatch(applySearch({ searchTerm: searchTermValue }));
    }
  };

  const apiParams = buildApiParams(1);

  const { filters: filterValues = {}, selected: selectedValues } = apiParams;
  const {
    group_by = null,
    break_down = null,
    user_activity_break_down = null,
    user_activity_group_by = null,
  } = filterValues as {
    group_by?: string | null;
    break_down?: string | null;
    user_activity_break_down?: string | null;
    user_activity_group_by?: string | null;
  };

  const drilldown = getDrilldownParams(selectedValues);

  const {
    group_by: GroupBy = null,
    type = '',
    yAxis = '',
    dt = '',
    g1 = '',
  } = selected?.row?.original || {};

  const groups = selected?.row?.original;

  const drilldownContext = {
    type: type || selected?.type,
    metric: drilldownContentATS?.[selected?.id] || 'Activity Details',
    totalCount: totalCount || 0,
    yAxis: yAxis,
    xAxis: dt,
    chartValue: g1,
    g1Header: groups?.parent
      ? groups?.parent?.name || groups?.parent?.title || groups?.parent
      : GroupBy?.name || GroupBy?.title || GroupBy,
    g2Header: groups?.parent
      ? GroupBy?.name || GroupBy?.title || GroupBy
      : null,
    metricValue: groups[selected?.id],
  };

  const { metric_name, dt_display_name, g1_display_name, g2_display_name } =
    getDrilldownContext({ context: drilldownContext });

  // Modal content
  const renderModalContent = () => (
    <>
      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        drilldownData={drilldownData}
        reportData={{
          timeFrame: {
            date_group: filterParams?.date_group || filters?.date_group,
            from: filterParams?.filter_by?.date?.from || filters?.from,
            to: filterParams?.filter_by?.date?.to || filters?.to,
          },
          group: {
            group_by_1: group_by || user_activity_group_by,
            group_by_2: break_down || user_activity_break_down,
          },
          // filters: filterParams,
          tile: tile,
          query_type: 'drilldown',
          drilldown: {
            ...drilldown,
            metric_name: metric_name,
            g1_display_name: g1_display_name,
            g2_display_name: g2_display_name,
            dt_display_name: dt_display_name,
          },
        }}
        initialLoading={initialLoading}
      />
      <ContentArea
        isLoading={isLoading}
        currentDrillPage={currentDrillPage}
        drilldownData={drilldownData}
        totalCount={totalCount}
        handleLoadMore={handleLoadMore}
        modalSize={modalSize}
        columnConfig={columnConfig}
        initialLoading={initialLoading}
      />
    </>
  );

  const renderFooter = () => {
    return (
      <Flex
        justifyContent="space-between"
        p="16px 32px"
        width="100%"
        alignItems="center"
        borderTopWidth="1px"
        borderColor="border-color"
      >
        <KText
          maxW={modalSize === 'full' ? '' : 'calc(100% - 150px)'}
          isTruncated
          enableTooltip={true}
        >
          <KText display="inline" size="sm-semi-bold">
            Note:{' '}
          </KText>
          Metrics are based on multiple data points, so they may differ from the
          row count
        </KText>

        <Flex justifyContent="flex-end" flex="1">
          <KText size="sm-medium">
            {displayedItems} out of {totalCount}
          </KText>
        </Flex>
      </Flex>
    );
  };

  return (
    <KModal
      isOpen={isOpen}
      onClose={() => {
        onClose();
        dispatch(resetDrill());
      }}
      modalContentProps={{
        minW: modalSize === 'full' ? '100vw' : 'min-content',
        minHeight: modalSize === 'full' ? '100vh' : '400px',
        maxW: modalSize === 'full' ? '100vw' : '800px',
        maxH: modalSize === 'full' ? '100vh' : '80vh',
      }}
      title={
        <DrilldownHeader
          selected={selected}
          totalCount={totalCount}
          modalSize={modalSize}
          onToggleModalSize={toggleModalSize}
          onMouseDown={handleMouseDown}
        />
      }
      footer={renderFooter()}
      modalBodyProps={{ p: '0px 24px', flex: '1', overflow: 'auto' }}
    >
      {renderModalContent()}
    </KModal>
  );
};
